import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'

export interface FormFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  error?: string
  hint?: string
  startIcon?: ReactNode
  trailing?: ReactNode
  /** Draws the teal "field accepted" border used by the Flutter form. */
  valid?: boolean
}

/**
 * Labelled input with error/hint wiring (`aria-invalid`, `aria-describedby`).
 * Copy is passed in already translated by the screen.
 */
export function FormField({
  label,
  error,
  hint,
  startIcon,
  trailing,
  valid = false,
  className,
  ...inputProps
}: FormFieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <div className={startIcon || trailing ? 'relative' : undefined}>
        {startIcon ? (
          <span
            className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted"
            aria-hidden="true"
          >
            {startIcon}
          </span>
        ) : null}
        <input
          {...inputProps}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={[
            'field-input',
            error ? 'border-danger' : '',
            valid && !error ? 'border-teal' : '',
            startIcon ? 'pl-11' : '',
            trailing ? 'pr-12' : '',
            className ?? '',
          ]
            .join(' ')
            .trim()}
        />
        {trailing ? (
          <span className="absolute inset-y-0 right-1 flex items-center">
            {trailing}
          </span>
        ) : null}
      </div>
      {hint ? (
        <p id={hintId} className="mt-1.5 text-xs leading-snug text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          className="mt-1.5 text-sm font-semibold text-danger"
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}

interface TextAreaFieldProps {
  label: string
  error?: string
  hint?: string
  rows?: number
  maxLength?: number
  autoComplete?: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  name?: string
}

export function TextAreaField({
  label,
  error,
  hint,
  rows = 3,
  maxLength,
  autoComplete,
  placeholder,
  value,
  onChange,
  onBlur,
  name,
}: TextAreaFieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        name={name}
        rows={rows}
        maxLength={maxLength}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={[
          'field-input resize-y',
          error ? 'border-danger' : '',
        ]
          .join(' ')
          .trim()}
      />
      {hint ? (
        <p id={hintId} className="mt-1.5 text-xs leading-snug text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="mt-1.5 text-sm font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
